import { useCallback, useEffect, useRef, useState } from "react";
import { Bold, Italic, List, ListOrdered, Link2, Paperclip, X, Loader2, FileText, File as FileIcon } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * Lightweight rich-text note editor: bold / italic / bullet & numbered lists /
 * links, @mentions, plus screenshot-paste and file-upload attachments.
 * Content is edited as HTML; attachments are managed as a separate array.
 */
export function RichNoteEditor({
  html = "", onChange, mentions = [], onMentionsChange,
  attachments = [], onAttachmentsChange, module,
  placeholder = "Write a note…", testId = "rich-note-editor", onSubmitShortcut, autoFocus = false,
}) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const focused = useRef(false);
  const [candidates, setCandidates] = useState([]);
  const [dd, setDd] = useState({ open: false, items: [], active: 0 });
  const [uploading, setUploading] = useState(false);

  const ensureCandidates = useCallback(async () => {
    if (!module) return [];
    try {
      const { data } = await api.get("/notes/mention-candidates", { params: { module } });
      const list = data.candidates || [];
      setCandidates(list);
      return list;
    } catch { return []; }
  }, [module]);

  useEffect(() => { setCandidates([]); }, [module]);

  // Sync editor DOM when html is reset externally (e.g. after posting).
  useEffect(() => {
    const el = ref.current;
    if (el && !focused.current && html !== el.innerHTML) el.innerHTML = html || "";
  }, [html]);

  useEffect(() => { if (autoFocus && ref.current) ref.current.focus(); }, [autoFocus]);

  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); ref.current?.focus(); emit(); };

  const makeLink = () => {
    const url = window.prompt("Link URL");
    if (!url) return;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    document.execCommand("createLink", false, href);
    ref.current?.focus(); emit();
  };

  const emit = () => {
    const el = ref.current; if (!el) return;
    onChange?.(el.innerHTML);
    const seen = {};
    const ms = [...el.querySelectorAll("span.mention[data-user-id]")]
      .map((s) => ({ user_id: s.getAttribute("data-user-id"), name: s.textContent.replace(/^@/, "") }))
      .filter((m) => !seen[m.user_id] && (seen[m.user_id] = 1));
    onMentionsChange?.(ms);
  };

  const detectMention = async () => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return setDd((d) => ({ ...d, open: false }));
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== 3) return setDd((d) => ({ ...d, open: false }));
    const before = node.textContent.slice(0, range.startOffset);
    const m = before.match(/@([^\s@]*)$/);
    if (!m) return setDd((d) => ({ ...d, open: false }));
    const query = m[1].toLowerCase();
    const list = candidates.length ? candidates : await ensureCandidates();
    const items = list.filter((c) => c.name.toLowerCase().includes(query)).slice(0, 6);
    setDd(items.length ? { open: true, items, active: 0 } : { open: false, items: [], active: 0 });
  };

  const insertMention = (cand) => {
    const el = ref.current; el.focus();
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType === 3) {
      const text = node.textContent; const caret = range.startOffset;
      const mm = text.slice(0, caret).match(/@([^\s@]*)$/);
      if (mm) {
        const start = caret - mm[0].length;
        node.textContent = text.slice(0, start) + text.slice(caret);
        range.setStart(node, start); range.setEnd(node, start);
      }
    }
    const span = document.createElement("span");
    span.className = "mention"; span.setAttribute("data-user-id", cand.user_id);
    span.setAttribute("contenteditable", "false"); span.textContent = `@${cand.name}`;
    range.insertNode(span);
    const space = document.createTextNode("\u00A0");
    span.after(space);
    const r2 = document.createRange(); r2.setStartAfter(space); r2.collapse(true);
    sel.removeAllRanges(); sel.addRange(r2);
    setDd({ open: false, items: [], active: 0 });
    emit();
  };

  const onKeyDown = (e) => {
    if (dd.open) {
      if (e.key === "ArrowDown") { e.preventDefault(); setDd((d) => ({ ...d, active: (d.active + 1) % d.items.length })); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setDd((d) => ({ ...d, active: (d.active - 1 + d.items.length) % d.items.length })); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(dd.items[dd.active]); return; }
      if (e.key === "Escape") { setDd({ open: false, items: [], active: 0 }); return; }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); onSubmitShortcut?.(); }
  };

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const added = [];
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/notes/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(data);
      } catch (e) { toast.error(e.response?.data?.detail || `Couldn't upload ${file.name}`); }
    }
    if (added.length) onAttachmentsChange?.([...(attachments || []), ...added]);
    setUploading(false);
  };

  const onPaste = async (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type && it.type.startsWith("image/")) {
        e.preventDefault();
        const file = it.getAsFile();
        if (file) await uploadFiles([new File([file], `screenshot-${Date.now()}.png`, { type: file.type })]);
        return;
      }
    }
    const text = e.clipboardData.getData("text/plain");
    if (text) { e.preventDefault(); document.execCommand("insertText", false, text); }
  };

  const removeAttachment = (fid) => onAttachmentsChange?.((attachments || []).filter((a) => a.file_id !== fid));

  const TBtn = ({ cmd, onClick, icon: Icon, label }) => (
    <button type="button" data-testid={`${testId}-${label}`} title={label} onMouseDown={(e) => e.preventDefault()}
      onClick={onClick || (() => exec(cmd))}
      className="p-1.5 rounded-md text-zinc-400 hover:text-indigo-300 hover:bg-zinc-800 transition-colors">
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 focus-within:border-indigo-500/60 transition-colors">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-zinc-800">
        <TBtn cmd="bold" icon={Bold} label="bold" />
        <TBtn cmd="italic" icon={Italic} label="italic" />
        <span className="w-px h-5 bg-zinc-800 mx-1" />
        <TBtn cmd="insertUnorderedList" icon={List} label="bullet-list" />
        <TBtn cmd="insertOrderedList" icon={ListOrdered} label="numbered-list" />
        <span className="w-px h-5 bg-zinc-800 mx-1" />
        <TBtn onClick={makeLink} icon={Link2} label="link" />
        <TBtn onClick={() => fileRef.current?.click()} icon={Paperclip} label="attach" />
        {uploading && <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-400 ml-1" />}
        <input ref={fileRef} type="file" multiple hidden data-testid={`${testId}-file-input`}
          onChange={(e) => { uploadFiles([...e.target.files]); e.target.value = ""; }} />
      </div>

      <div className="relative">
        <div
          ref={ref}
          data-testid={testId}
          contentEditable
          suppressContentEditableWarning
          onInput={() => { emit(); detectMention(); }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onFocus={() => { focused.current = true; }}
          onBlur={() => { focused.current = false; setTimeout(() => setDd({ open: false, items: [], active: 0 }), 150); }}
          data-placeholder={placeholder}
          className="rte-editor min-h-[72px] max-h-64 overflow-y-auto px-3 py-2.5 text-sm text-zinc-100 outline-none"
        />
        {dd.open && (
          <div className="absolute left-3 top-full mt-1 z-50 w-56 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl overflow-hidden" data-testid={`${testId}-mention-dd`}>
            {dd.items.map((c, i) => (
              <button key={c.user_id} type="button" onMouseDown={(e) => { e.preventDefault(); insertMention(c); }}
                className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-colors ${i === dd.active ? "bg-indigo-600/20 text-indigo-200" : "text-zinc-300 hover:bg-zinc-800"}`}>
                <span className="text-indigo-400">@</span>{c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {(attachments || []).length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pb-3 pt-1" data-testid={`${testId}-attachments`}>
          {attachments.map((a) => (
            <div key={a.file_id} className="relative group" data-testid={`${testId}-att-${a.file_id}`}>
              {a.is_image ? (
                <img src={mediaUrl(a.url)} alt={a.filename} className="h-16 w-16 object-cover rounded-lg border border-zinc-800" />
              ) : (
                <div className="flex items-center gap-2 h-16 px-3 rounded-lg border border-zinc-800 bg-zinc-900 max-w-[180px]">
                  {a.content_type === "application/pdf" ? <FileText className="h-5 w-5 text-red-400 flex-shrink-0" /> : <FileIcon className="h-5 w-5 text-zinc-400 flex-shrink-0" />}
                  <span className="text-xs text-zinc-300 truncate">{a.filename}</span>
                </div>
              )}
              <button type="button" data-testid={`${testId}-att-remove-${a.file_id}`} onClick={() => removeAttachment(a.file_id)}
                className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
